/**
 * VALUATION-B3 guardrail 1 / VAL-INTAKE-SCOPE-1 §2 — the value-driver intake
 * surface, as a guided three-step wizard.
 *
 * This component replaces `DriverScoreSection`, which rendered one number box
 * per signed driver. The guiding principle it implements, verbatim:
 *
 *   "Drivers are deterministic from the answered questionnaire — the
 *    value-driver score is computed by the engine from the intake answers,
 *    not typed by the valuator."
 *
 * So there is no free-text or numeric input anywhere below. The seller can
 * only pick one of the answer options the ACTIVE config offers, and what goes
 * on the wire is `question_id -> option value`. The score is recomputed
 * server-side by `score_questionnaire()`; the running total shown here is a
 * PREVIEW rendered from the same option points, and it is never sent.
 *
 * VAL-INTAKE-SCOPE-1 R2 reworked this from a flat 25-question accordion into
 * three steps — one per config section, in the fixed order a seller should be
 * walked through (revenue quality, then workforce & operations, then risk /
 * growth / market context) — and split each question's weight into
 * `required` vs corroborating (`DriverQuestion.required`, config data, never
 * a hardcoded question-id list here):
 *
 *   * A required question (marked *) is what `score_questionnaire()` actually
 *     scores; the Next button for a step is gated on every required question
 *     in THAT step being answered, and names the specific question(s) still
 *     missing rather than a generic "complete this section."
 *   * A corroborating question is still asked and still recorded, but never
 *     blocks progression and never moves the preview total (mirrors the
 *     engine's own S2 fence — see driver_questionnaire.py).
 *   * Help text moved from always-inline to an accessible disclosure button
 *     (`aria-expanded`, keyboard reachable) so a required question's options
 *     aren't pushed below the fold by a paragraph nobody asked for yet.
 *
 * VAL-INTAKE-SCOPE-1 §3 added the "I'm not sure" control (`_DriverQuestionDefer.tsx`)
 * — separate from the answer options (R3a); `deferred` / `attachments` never enter
 * the answers payload (R3d) but autosave to the draft with `persist` (SAVE-ON-TYPE-2).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import type { DriverQuestion, DriverQuestionnaire } from '@/types/valuation'
import { humanizeDriver } from './_IntakeDrivers'
import { DeferControl, isDocumentAnswerable, type DeferredAttachment } from './_DriverQuestionDefer'
import { useDeferredQuestions, type DeferredPersist } from './_useDraftExtras'

const SECTION_LABELS: Record<string, string> = {
  revenue_quality: 'Revenue quality',
  workforce_operations: 'Workforce & operations',
  risk_growth_context: 'Risk, growth & market context',
}

// R2b — the order a seller is walked through. NOT the config's own
// `sections` order (QUESTIONNAIRE_SECTIONS in core/valuation/config_schema.py
// is engine/back-compat order and answers a different question: it does not
// claim to be the right order to ask a person).
const STEP_ORDER = ['revenue_quality', 'workforce_operations', 'risk_growth_context']

function sectionLabel(section: string): string {
  return SECTION_LABELS[section] ?? humanizeDriver(section)
}

/** R2e — help text behind a keyboard-reachable disclosure, not hover-only. */
function HelpToggle({ text }: { text: string }) {
  const [open, setOpen] = useState(false)
  if (!text) return null
  return (
    <div className="mb-3">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className="text-xs font-medium text-gray-500 underline underline-offset-2 hover:text-gray-700"
      >
        {open ? 'Hide why we ask' : 'Why we ask'}
      </button>
      {open && <p className="mt-1 text-xs text-gray-500">{text}</p>}
    </div>
  )
}

function QuestionBlock({
  question,
  answer,
  onAnswer,
  deferred,
  attachment,
  onDefer,
  onCancelDefer,
  onAttach,
  canSeePoints,
}: {
  question: DriverQuestion
  answer: string | undefined
  onAnswer: (questionId: string, value: string) => void
  deferred: boolean
  attachment: DeferredAttachment | undefined
  onDefer: (questionId: string) => void
  onCancelDefer: (questionId: string) => void
  onAttach: (questionId: string, file: DeferredAttachment) => void
  /** R3 — the seller (valuation_owner) never sees which answer scores
   * higher; valuation_signer/firm/admin need the scoring key to review a run.
   * Removed from the DOM entirely when false, not merely CSS-hidden. */
  canSeePoints: boolean
}) {
  const derived = isDocumentAnswerable(question)
  // R3e/S18 -- a deferred document-answerable question cannot be resolved by
  // hand. Radios stay disabled until a document is attached. Branch 2 never
  // disables (R3f -- there is no document path to wait on).
  const radiosDisabled = derived && deferred && !attachment
  return (
    // R11 — no score number renders here for any role; `canSeePoints` is
    // kept wired through as a real DOM signal (not a display) rather than
    // deleted, since it is still the correct place to hang a future
    // role-gated scoring-key feature if one comes back.
    <fieldset
      id={`question-${question.question_id}`}
      tabIndex={-1}
      className="rounded-lg border border-gray-200 p-3"
      data-scoring-key-visible={canSeePoints}
    >
      <legend className="px-1 text-sm font-medium text-gray-900">
        {question.prompt}
        {question.required && (
          <>
            <span aria-hidden="true" className="text-red-600"> *</span>
            <span className="sr-only"> (required)</span>
          </>
        )}
      </legend>
      <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
        <span className="rounded bg-gray-100 px-1.5 py-0.5">
          {humanizeDriver(question.driver)}
        </span>
        {!question.required && (
          <span className="rounded bg-blue-50 px-1.5 py-0.5 text-blue-700">
            corroborating — doesn't change the score
          </span>
        )}
        {derived && (
          <span
            className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700"
            title="The documents on file should answer this — confirm against them rather than estimating."
          >
            check the documents
          </span>
        )}
      </div>
      <HelpToggle text={question.help_text} />
      <DeferControl
        question={question}
        deferred={deferred}
        attachment={attachment}
        onDefer={onDefer}
        onCancelDefer={onCancelDefer}
        onAttach={onAttach}
      />
      <div className={`space-y-1.5 ${radiosDisabled ? 'opacity-50' : ''}`}>
        {question.options.map(opt => {
          const checked = answer === opt.value
          return (
            <label
              key={opt.value}
              className={`flex items-start gap-2 rounded-md border p-2 text-sm ${
                radiosDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
              } ${checked ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:bg-gray-50'}`}
            >
              <input
                type="radio"
                className="mt-1 shrink-0"
                name={question.question_id}
                value={opt.value}
                checked={checked}
                disabled={radiosDisabled}
                onChange={() => onAnswer(question.question_id, opt.value)}
              />
              <span className="flex-1 text-gray-900">{opt.label}</span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

export function DriverQuestionnaireSection({
  questionnaire,
  answers,
  onAnswer,
  focusQuestionId,
  persist,
}: {
  questionnaire: DriverQuestionnaire
  answers: Record<string, string>
  onAnswer: (questionId: string, value: string) => void
  /** VAL-POLISH-1 item 5 — from useResumeScroll(), the first unanswered
   * required question at load, or null. Jumps to its step once and focuses
   * it; never re-fires on later edits. */
  focusQuestionId?: string | null
  persist?: DeferredPersist
}) {
  const { user } = useAuth()
  // R3 -- hide the scoring key from the seller (valuation_owner). Every other
  // role (valuation_signer, firm/admin) sees it unchanged, same as today.
  const canSeePoints = user?.role !== 'valuation_owner'

  const bySection = useMemo(() => {
    const map = new Map<string, DriverQuestion[]>()
    for (const section of questionnaire.sections) map.set(section, [])
    for (const q of questionnaire.questions) {
      // A question in a section the config did not declare still has to be
      // askable — dropping it would silently lower the achievable score.
      if (!map.has(q.section)) map.set(q.section, [])
      map.get(q.section)!.push(q)
    }
    return map
  }, [questionnaire])

  // The fixed seller-facing step order, filtered to sections that actually
  // have questions, with any section the config declares that this list
  // doesn't know about appended rather than dropped — same reasoning as
  // `bySection` above.
  const steps = useMemo(() => {
    const known = STEP_ORDER.filter(s => (bySection.get(s)?.length ?? 0) > 0)
    const extra = [...bySection.keys()].filter(
      s => !STEP_ORDER.includes(s) && (bySection.get(s)?.length ?? 0) > 0,
    )
    return [...known, ...extra]
  }, [bySection])

  const [step, setStep] = useState(0)
  // §3 -- `deferred` marks "I'm not sure"; `attachments` the filename attached
  // for a document-answerable question (S15/R3c). Saved to the draft if `persist`.
  const { deferred, setDeferred, attachments, setAttachments } = useDeferredQuestions(persist)

  // R2 (item 5) -- runs once, when focusQuestionId first becomes non-null
  // (Cowork review: driver answers hydrate after the questionnaire, so a
  // useState initializer here ran before the real target was known). Jumps
  // to its step, then scrolls/focuses it once that step is in the DOM.
  const resumeScrolledRef = useRef(false)
  useEffect(() => {
    if (resumeScrolledRef.current || !focusQuestionId) return
    const idx = steps.findIndex(s => (bySection.get(s) ?? []).some(q => q.question_id === focusQuestionId))
    if (idx !== -1 && idx !== step) {
      setStep(idx)
      return
    }
    resumeScrolledRef.current = true
    const el = document.getElementById(`question-${focusQuestionId}`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el?.focus()
  }, [focusQuestionId, step, steps, bySection])

  function handleDefer(questionId: string) {
    setDeferred(prev => ({ ...prev, [questionId]: true }))
    const question = questionnaire.questions.find(q => q.question_id === questionId)
    if (question && isDocumentAnswerable(question)) {
      // R3e -- deferring clears any prior hand-typed answer; a deferred
      // document-answerable question is unresolved, not silently pre-filled.
      onAnswer(questionId, '')
      setAttachments(prev => {
        const next = { ...prev }
        delete next[questionId]
        return next
      })
    }
  }

  function handleCancelDefer(questionId: string) {
    setDeferred(prev => ({ ...prev, [questionId]: false }))
  }

  function handleAttach(questionId: string, file: DeferredAttachment) {
    setAttachments(prev => ({ ...prev, [questionId]: file }))
  }

  // S17 -- a deferred, document-answerable question with nothing attached
  // yet is blocked for a different reason than a plain unanswered question:
  // it's waiting on a document, not a typed answer (R3d: same engine rule,
  // different reason shown to the seller).
  function isDeferredAwaitingDocument(question: DriverQuestion): boolean {
    return Boolean(deferred[question.question_id]) && isDocumentAnswerable(question) && !attachments[question.question_id]
  }

  function describeMissing(question: DriverQuestion): string {
    return isDeferredAwaitingDocument(question)
      ? `${question.prompt} (document required)`
      : question.prompt
  }

  const clampedStep = Math.min(step, Math.max(0, steps.length - 1))
  const currentSection = steps[clampedStep]
  const currentQuestions = currentSection ? bySection.get(currentSection) ?? [] : []

  const requiredHere = currentQuestions.filter(q => q.required)
  const missingRequiredHere = requiredHere.filter(q => (answers[q.question_id] ?? '') === '')
  const answeredRequiredHere = requiredHere.length - missingRequiredHere.length
  const canAdvance = missingRequiredHere.length === 0
  const isLastStep = clampedStep === steps.length - 1

  const allRequired = questionnaire.questions.filter(q => q.required)
  const missingRequiredOverall = allRequired.filter(q => (answers[q.question_id] ?? '') === '')

  return (
    <Card>
      <CardHeader>
        <CardTitle>Value drivers</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* R2a — disclosure, step 1 only. No completion-time estimate; the
            required count is read off the config, not hardcoded, so this
            stays true whether v1 (all required) or v2 (11 of 25) is active.
            R2f — the "rest corroborate" sentence only makes sense when a
            "rest" exists; under an all-required config (v1) it is dropped
            rather than claiming a corroborating set of zero. */}
        {clampedStep === 0 && (
          <div className="space-y-1 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
            <p>
              {questionnaire.questions.length} questions. {allRequired.length} are required —
              marked *.
              {allRequired.length < questionnaire.questions.length &&
                ' The rest corroborate what you already told us; answering them helps but they never move the score.'}
            </p>
            <p>Score conservatively: when a reading is between two options, the lower one wins.</p>
            <p>Don't guess — mark "I'm not sure" and we'll say which document settles it.</p>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="font-medium text-gray-900">
            Step {clampedStep + 1} of {steps.length}: {sectionLabel(currentSection ?? '')}
          </span>
          {/* R2b — progress reads "N of M required" for THIS step, never a
              share of all 25/all-questions. */}
          <span className="text-gray-500">
            {answeredRequiredHere} of {requiredHere.length} required
          </span>
        </div>

        <div className="space-y-3">
          {currentQuestions.map(q => (
            <QuestionBlock
              key={q.question_id}
              question={q}
              answer={answers[q.question_id]}
              onAnswer={onAnswer}
              deferred={deferred[q.question_id] ?? false}
              attachment={attachments[q.question_id]}
              onDefer={handleDefer}
              onCancelDefer={handleCancelDefer}
              onAttach={handleAttach}
              canSeePoints={canSeePoints}
            />
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-3">
          <Button
            type="button"
            variant="outline"
            disabled={clampedStep === 0}
            onClick={() => setStep(s => Math.max(0, s - 1))}
          >
            Back
          </Button>

          {!isLastStep ? (
            <div className="flex flex-col items-end gap-1">
              {/* R2c — disabled until every required question in THIS step is
                  answered; corroborating questions never block. */}
              <Button
                type="button"
                disabled={!canAdvance}
                onClick={() => setStep(s => Math.min(steps.length - 1, s + 1))}
              >
                Next
              </Button>
              {!canAdvance && (
                <p className="max-w-sm text-right text-xs text-amber-700">
                  Answer to continue: {missingRequiredHere.map(q => describeMissing(q)).join('; ')}
                </p>
              )}
            </div>
          ) : missingRequiredOverall.length > 0 ? (
            <span className="text-xs text-amber-700">
              {missingRequiredOverall.length} required question
              {missingRequiredOverall.length === 1 ? '' : 's'} still unanswered — the run is
              blocked until every required question has an answer
              {missingRequiredOverall.some(q => isDeferredAwaitingDocument(q)) &&
                ' (some are waiting on a document, not a typed answer)'}
            </span>
          ) : (
            // R11 — no score number here for any role; scoring happens
            // server-side and is never previewed on this screen.
            <span className="text-sm font-semibold text-gray-900">
              All required questions answered
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
